/**
 * Utility class to manage saving and restoring input field values
 * when fields are disabled/enabled based on conditions
 */
export class InputValueManager<T = any> {
  private savedValues: Map<string, T> = new Map();

  /**
   * Save the current value of a field and optionally clear it
   * @param fieldName - Unique identifier for the field
   * @param currentValue - The current value to save
   * @param clearValue - Whether to return null to clear the field (default: true)
   * @returns The value to set (null if clearValue is true, otherwise the current value)
   */
  saveAndClear(fieldName: string, currentValue: T, clearValue: boolean = true): T | null {
    this.savedValues.set(fieldName, currentValue);
    return clearValue ? null : currentValue;
  }

  /**
   * Restore the previously saved value for a field
   * @param fieldName - Unique identifier for the field
   * @param defaultValue - Default value to return if no saved value exists
   * @returns The restored value or the default value
   */
  restore(fieldName: string, defaultValue: T | null = null): T | null {
    if (this.savedValues.has(fieldName)) {
      const value = this.savedValues.get(fieldName);
      this.savedValues.delete(fieldName);
      return value;
    }
    return defaultValue;
  }

  /**
   * Handle a field's value based on a condition change
   * @param fieldName - Unique identifier for the field
   * @param currentValue - The current value of the field
   * @param shouldDisable - Whether the field should be disabled (and value cleared)
   * @param previouslyDisabled - The previous disabled state
   * @returns The value to set on the field
   */
  handleValueOnStateChange(
    fieldName: string,
    currentValue: T,
    shouldDisable: boolean,
    previouslyDisabled: boolean,
  ): T | null {
    // State hasn't changed, return current value
    if (shouldDisable === previouslyDisabled) {
      return currentValue;
    }

    // Becoming disabled - save and clear
    if (shouldDisable) {
      return this.saveAndClear(fieldName, currentValue);
    }

    // Becoming enabled - restore
    return this.restore(fieldName, currentValue);
  }
}
